import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ShieldCheck, Leaf, CalendarClock, ArrowDown } from 'lucide-react';

const HEADLINE = ['Sparkling homes.', 'Thriving workplaces.', 'One standard: spotless.'];

const lineVariants = {
  hidden: { y: '115%' },
  show: (i) => ({
    y: '0%',
    transition: { duration: 1, delay: 0.25 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yImg1 = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yImg2 = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-obsidian grain overflow-hidden flex items-center"
    >
      <div className="absolute inset-0 hero-grid-bg" />
      <div className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full bg-mint/15 blur-[140px]" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-mint/10 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 lg:pt-24 lg:pb-0 w-full grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <h1 className="font-display font-extrabold text-white leading-[1.04] tracking-tight text-[11.5vw] sm:text-6xl lg:text-7xl xl:text-[5rem]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 2 ? 'text-mint-vibrant' : ''}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 text-stone-300/90 text-base sm:text-lg leading-relaxed max-w-xl"
          >
            From family homes to offices, restaurants and retail across Surrey, Vancouver and
            the Lower Mainland — we deliver deep, dependable cleaning on your schedule.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-cta-primary"
              className="group bg-mint hover:bg-mint-deep text-white font-semibold text-sm sm:text-base px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.03] active:scale-95 shadow-[0_8px_40px_rgba(160,113,78,0.45)]"
            >
              Get My Free Quote
              <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">→</span>
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="text-white/90 hover:text-white text-sm sm:text-base font-medium px-8 py-4 rounded-full border border-white/20 hover:border-white/50 transition-all duration-300"
            >
              Explore Our Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              { icon: ShieldCheck, label: 'Insured & bonded' },
              { icon: Leaf, label: 'Eco-friendly products' },
              { icon: CalendarClock, label: 'Flexible scheduling, 7 days a week' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <s.icon size={15} className="text-mint-vibrant" />
                <span className="text-[13px] text-stone-300 font-medium">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden sm:block pb-10 lg:pb-0">
          <motion.div style={{ y: yImg1 }} className="relative z-10 w-[72%] ml-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-t-[10rem] rounded-b-3xl overflow-hidden border border-white/15 shadow-2xl shadow-black/50"
            >
              <img
                src="https://images.pexels.com/photos/6195274/pexels-photo-6195274.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=560"
                alt="Residential cleaning team at work in a living room"
                className="w-full h-[420px] object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 1.35 }}
              className="absolute -bottom-6 left-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl px-5 py-4 z-20"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mint-vibrant">
                Available
              </p>
              <p className="text-white font-semibold text-sm mt-1">7 days · 8:00 AM – 11:00 PM</p>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: yImg2 }} className="absolute -bottom-2 left-0 w-[44%] z-0">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl overflow-hidden border border-white/15 shadow-2xl shadow-black/50"
            >
              <img
                src="https://images.pexels.com/photos/33357392/pexels-photo-33357392.png?auto=compress&cs=tinysrgb&dpr=2&h=440&w=380"
                alt="Commercial cleaning crew at an office entrance"
                className="w-full h-[280px] object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-stone-400"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>
    </section>
  );
}
